import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileSpreadsheet, FileText, Receipt, Send, Timer } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { toast } from "sonner";

function firstOfMonth() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;
}

function downloadWithToken(url, filename) {
  const token = localStorage.getItem("elswitch_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const href = URL.createObjectURL(blob);
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    })
    .catch((e) => toast.error(`Download failed: ${e.message || e}`));
}

export default function PlatformBilling() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [mailLog, setMailLog] = useState([]);
  const [runningScheduler, setRunningScheduler] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/platform/billing/overview?from=${from}&to=${to}`)
      .then((r) => setRows(r.data.orgs || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };

  const loadLog = () => {
    api.get("/platform/billing/email-log?limit=25").then((r) => setMailLog(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendForOrg = async (orgId) => {
    setSendingId(orgId);
    try {
      const { data } = await api.post("/platform/billing/send-report", {
        org_id: orgId,
        from,
        to,
      });
      toast.success(t("billing.sentOk", { id: data?.provider_id || "" }));
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.sendFailed"));
    } finally {
      setSendingId(null);
    }
  };

  const runSchedulerNow = async () => {
    setRunningScheduler(true);
    try {
      const { data } = await api.post("/platform/billing/run-scheduler-now");
      toast.success(
        `Dispatcher ran: ${data.sent} sent / ${data.skipped} skipped / ${data.failed} failed`
      );
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunningScheduler(false);
    }
  };

  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({
          calls: a.calls + (r.totals?.calls || 0),
          minutes: a.minutes + (r.totals?.minutes || 0),
          peak: Math.max(a.peak, r.totals?.max_peak_channels || 0),
        }),
        { calls: 0, minutes: 0, peak: 0 }
      ),
    [rows]
  );

  return (
    <div data-testid="page-platform-billing">
      <PageHeader
        title="Platform Billing"
        subtitle="Peak channels and talk minutes across all tenants. Download per-tenant PDF/CSV reports."
        icon={Receipt}
        actions={
          <Button
            variant="outline"
            onClick={runSchedulerNow}
            disabled={runningScheduler}
            data-testid="pbilling-run-scheduler"
          >
            <Timer className="h-4 w-4 mr-1" />
            {runningScheduler ? t("billing.sending") : "Run dispatcher now"}
          </Button>
        }
      />

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="pbilling-from"
            />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="pbilling-to"
            />
          </div>
          <Button onClick={load} disabled={loading} data-testid="pbilling-apply">
            {loading ? "Loading…" : "Apply"}
          </Button>
          <div className="ml-auto flex items-center gap-6 text-xs text-muted-foreground">
            <div>
              Combined calls:{" "}
              <span className="font-mono text-foreground text-sm">{totals.calls}</span>
            </div>
            <div>
              Combined minutes:{" "}
              <span className="font-mono text-foreground text-sm">{totals.minutes.toFixed(1)}</span>
            </div>
            <div>
              Highest peak:{" "}
              <span className="font-mono text-foreground text-sm">{totals.peak}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="No tenants yet" description="Once tenants exist and receive calls, usage will appear here." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Minutes</TableHead>
                  <TableHead className="text-right">Peak channels</TableHead>
                  <TableHead className="text-right w-[220px]">Reports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const peak = r.totals?.max_peak_channels || 0;
                  const overage = r.plan_limit > 0 && peak > r.plan_limit;
                  return (
                    <TableRow key={r.org_id} className="row-hover" data-testid={`pbilling-row-${r.org_id}`}>
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{r.slug}</div>
                      </TableCell>
                      <TableCell>
                        <StatusPill kind="info">{r.plan || "—"}</StatusPill>
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          kind={
                            r.status === "active"
                              ? "ok"
                              : r.status === "pending"
                              ? "warn"
                              : "danger"
                          }
                        >
                          {r.status}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.totals?.calls || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(r.totals?.minutes || 0).toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={overage ? "text-[hsl(var(--danger))] font-semibold" : ""}>
                          {peak}
                          {r.plan_limit > 0 && (
                            <span className="text-muted-foreground"> / {r.plan_limit}</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="mr-1"
                          onClick={() =>
                            downloadWithToken(
                              `${API_BASE}/platform/billing/report.csv?org_id=${r.org_id}&from=${from}&to=${to}`,
                              `elswitch-${r.slug}-${from}_${to}.csv`
                            )
                          }
                          data-testid={`pbilling-csv-${r.org_id}`}
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> CSV
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mr-1"
                          onClick={() =>
                            downloadWithToken(
                              `${API_BASE}/platform/billing/report.pdf?org_id=${r.org_id}&from=${from}&to=${to}`,
                              `elswitch-${r.slug}-${from}_${to}.pdf`
                            )
                          }
                          data-testid={`pbilling-pdf-${r.org_id}`}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => sendForOrg(r.org_id)}
                          disabled={sendingId === r.org_id}
                          data-testid={`pbilling-send-${r.org_id}`}
                        >
                          <Send className="h-3.5 w-3.5 mr-1" />
                          {sendingId === r.org_id ? t("billing.sending") : t("billing.sendNow")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="mt-4">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border text-sm font-medium flex items-center gap-2">
            <Send className="h-4 w-4 text-accent" /> {t("billing.recentSends")}
          </div>
          {mailLog.length === 0 ? (
            <div className="px-4 py-8 text-sm text-muted-foreground text-center">
              {t("billing.recentSendsEmpty")}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead>{t("billing.mailCols.when")}</TableHead>
                  <TableHead>{t("billing.mailCols.status")}</TableHead>
                  <TableHead>Org</TableHead>
                  <TableHead>{t("billing.mailCols.period")}</TableHead>
                  <TableHead>{t("billing.mailCols.recipients")}</TableHead>
                  <TableHead>{t("billing.mailCols.actor")}</TableHead>
                  <TableHead>{t("billing.mailCols.providerId")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mailLog.map((m) => (
                  <TableRow key={m.id} className="row-hover" data-testid={`mail-row-${m.id}`}>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {m.sent_at
                        ? new Date(m.sent_at).toLocaleString()
                        : new Date(m.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        kind={
                          m.status === "sent"
                            ? "ok"
                            : m.status === "pending"
                            ? "warn"
                            : "danger"
                        }
                      >
                        {m.status}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="font-mono text-[11px]">{m.org_name || m.org_id}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {m.period_from} → {m.period_to}
                    </TableCell>
                    <TableCell className="text-xs">{(m.recipients || []).join(", ")}</TableCell>
                    <TableCell className="text-xs">
                      {m.actor}
                      <span className="text-muted-foreground"> ({m.actor_scope})</span>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {m.provider_id || m.error || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
